import { clsx } from 'clsx';
import { LucideIcon } from 'lucide-react';

/**
 * Status Dot Component
 */
export interface StatusDotProps {
  variant: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  pulse?: boolean;
  className?: string;
}

export const StatusDot = ({ 
  variant, 
  size = 'md', 
  pulse = false,
  className = '' 
}: StatusDotProps) => {
  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
  };

  const variantClasses = {
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
    neutral: 'bg-gray-400',
  };

  return (
    <span className={clsx('relative inline-flex', className)}>
      <span className={clsx(
        'rounded-full',
        sizeClasses[size],
        variantClasses[variant]
      )} />
      {pulse && (
        <span className={clsx(
          'absolute inline-flex rounded-full opacity-75 animate-ping',
          sizeClasses[size],
          variantClasses[variant]
        )} />
      )}
    </span>
  );
};

/**
 * Status Badge with Dot
 */
export interface StatusBadgeProps {
  variant: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  label: string;
  icon?: LucideIcon;
  pulse?: boolean;
  size?: 'sm' | 'md';
}

export const StatusBadge = ({ 
  variant, 
  label, 
  icon: Icon,
  pulse = false,
  size = 'md'
}: StatusBadgeProps) => {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
  };

  const variantClasses = {
    success: 'bg-green-100 text-green-700 border-green-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    error: 'bg-red-100 text-red-700 border-red-200',
    info: 'bg-blue-100 text-blue-700 border-blue-200',
    neutral: 'bg-gray-100 text-gray-700 border-gray-200',
  };

  return (
    <span className={clsx(
      'inline-flex items-center gap-1.5 rounded-full font-medium border',
      sizeClasses[size],
      variantClasses[variant]
    )}>
      <StatusDot variant={variant} size="sm" pulse={pulse} />
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span>{label}</span>
    </span>
  );
};

/**
 * Status Indicator with Label
 */
export interface StatusIndicatorProps {
  variant: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  label: string;
  description?: string;
  pulse?: boolean;
}

export const StatusIndicator = ({ 
  variant, 
  label, 
  description,
  pulse = false 
}: StatusIndicatorProps) => {
  return (
    <div className="flex items-start gap-3">
      <StatusDot variant={variant} size="md" pulse={pulse} className="mt-1" />
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {description && (
          <p className="text-xs text-gray-600 mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
};

/**
 * Online/Offline Status
 */
export const OnlineStatus = ({ 
  online, 
  label 
}: { 
  online: boolean; 
  label?: string;
}) => {
  return (
    <div className="inline-flex items-center gap-2">
      <StatusDot 
        variant={online ? 'success' : 'neutral'} 
        size="sm" 
        pulse={online}
      />
      {label && (
        <span className="text-sm text-gray-600">
          {label || (online ? 'Online' : 'Offline')}
        </span>
      )}
    </div>
  );
};
